/**
 * Logout always clears browser-local auth state. When remote revocation cannot
 * be confirmed, it also persists a restoration-suppression marker so a later
 * reload cannot silently recreate the still-uncertain server session from the
 * HttpOnly refresh cookie. An explicit successful authentication clears it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { authControllerLogout } from './auth.api';
import { AUTH_CROSS_TAB_EVENT_KEY, useSession } from './session-provider';
import { setAccessToken } from '../../api/http-client';
import { setLocalSignOutSuppressed } from './local-signout';

const TABLE_PREFS_PREFIX = 'table-prefs:';
const SAVED_VIEWS_PREFIX = 'finance:saved-views:';
const EXPLICIT_CLIENT_KEYS = ['finance-platform-preferences', 'finance:theme', 'finance:device-preferences'];

function getDeviceKeys(userId?: string | null): string[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key) keys.push(key);
    }
    return keys.filter((key) => {
      if (EXPLICIT_CLIENT_KEYS.includes(key)) return true;
      if (key.startsWith(SAVED_VIEWS_PREFIX)) return true;
      if (key.startsWith(TABLE_PREFS_PREFIX)) {
        return !userId || key.includes(`:${userId}:`);
      }
      return false;
    });
  } catch {
    return [];
  }
}

function clearPersistedClientState(userId?: string | null): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    for (const key of getDeviceKeys(userId)) window.localStorage.removeItem(key);
  } catch {
    // Storage cleanup is best-effort and must not block logout.
  }
}

export interface LogoutResult {
  localSessionCleared: boolean;
  remoteRevocationSucceeded: boolean;
  error?: Error;
}

export function useLogout() {
  const { logout: sessionLogout, user } = useSession();
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Promise<LogoutResult> | null>(null);

  return useMutation({
    mutationFn: async (): Promise<LogoutResult> => {
      if (inFlightRef.current) return inFlightRef.current;

      const run = (async (): Promise<LogoutResult> => {
        let serverError: unknown;
        try {
          await authControllerLogout();
        } catch (err) {
          serverError = err;
        }

        let localSessionCleared = true;
        setAccessToken(null);
        clearPersistedClientState(user?.id);
        setLocalSignOutSuppressed(Boolean(serverError));
        try {
          await sessionLogout();
        } catch {
          localSessionCleared = false;
        }
        try {
          window.localStorage.setItem(AUTH_CROSS_TAB_EVENT_KEY, String(Date.now()));
          window.localStorage.removeItem(AUTH_CROSS_TAB_EVENT_KEY);
        } catch {
          // Cross-tab notification is best-effort; the active tab is cleared.
        }

        return {
          localSessionCleared,
          remoteRevocationSucceeded: !serverError,
          error: serverError
            ? (serverError instanceof Error ? serverError : new Error('Logout failed'))
            : undefined,
        };
      })();

      inFlightRef.current = run;
      try {
        return await run;
      } finally {
        inFlightRef.current = null;
      }
    },
    onSettled: () => {
      queryClient.clear();
    },
  });
}
